import React from 'react';
import noop from 'lodash/noop';
import Breadcrumbs, { BreadcrumbsItem } from '@atlaskit/breadcrumbs';
import { Box, xcss } from '@atlaskit/primitives';
import PageHeader from '@atlassian/jira-common-components-page-header/src/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	SCREEN,
	FireScreenAnalytics,
	FireTrackAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import type { ProjectContext } from '@atlassian/jira-router-resources-project-context/src/services/project-context/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useOutgoingMailConfig } from '../../../services/outgoing-mail-config/index.tsx';
import { EnableOutgoingMailInfo } from './enable-outgoing-mail-info/index.tsx';
import { messages } from './messages.tsx';
import { NotificationAppSubHeader } from './notification-app-sub-header/index.tsx';
import { NotificationTabs } from './notification-tabs/index.tsx';

const GetNotificationLogsBreadcrumbs = (
	projectContext: ProjectContext | null,
	breadcrumbRoot: string,
) => (
	<Breadcrumbs onExpand={noop}>
		<BreadcrumbsItem text={breadcrumbRoot} href="/jira/projects" />
		<BreadcrumbsItem
			text={`${projectContext?.projectName}`}
			href={`/browse/${projectContext?.projectKey}`}
		/>
	</Breadcrumbs>
);

export const NotificationLogsTabLayout = ({
	projectContext,
}: {
	projectContext: ProjectContext;
}) => {
	const { formatMessage } = useIntl();

	const breadcrumbRoot = formatMessage(messages.projects);
	const breadcrumbs = GetNotificationLogsBreadcrumbs(projectContext, breadcrumbRoot);

	const outgoingMailConfigData = useOutgoingMailConfig();
	const { outgoingMailConfig, error, loading } = outgoingMailConfigData;

	if (error) {
		fireErrorAnalytics({
			meta: {
				id: 'ErrorFetchingOutgoingMailConfigDataInTabsLayout',
				packageName: 'jiraSpaAppsServicedeskNotificationLogs',
				teamName: 'spartans',
			},
			error: new Error(error),
			sendToPrivacyUnsafeSplunk: true,
		});
	}

	return (
		<JSErrorBoundary
			id="CustomerNotificationLogsInTabsLayout"
			packageName="jiraSpaAppsServicedeskNotificationLogs"
			teamName="spartans"
		>
			<ContextualAnalyticsData sourceName="customerNotificationLogs" sourceType={SCREEN}>
				<Box xcss={notificationAppStyles}>
					<PageHeader breadcrumbs={breadcrumbs}>{formatMessage(messages.title)}</PageHeader>

					<>
						{!fg('unsuppress_customer_emails') && <NotificationAppSubHeader />}
						{outgoingMailConfig !== null && !error && !loading && !outgoingMailConfig?.enabled && (
							<>
								<FireTrackAnalytics eventName="LogsTableInTabsLayout OutGoingMailDisabledText" />
								<EnableOutgoingMailInfo />
							</>
						)}
					</>
					<NotificationTabs
						projectContext={projectContext}
						outgoingMailConfigData={outgoingMailConfigData}
					/>
				</Box>
				<FireScreenAnalytics />
			</ContextualAnalyticsData>
		</JSErrorBoundary>
	);
};

const notificationAppStyles = xcss({
	marginLeft: 'space.500',
	marginRight: 'space.500',
});
